
import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, ChevronDown, ChevronUp, X, Edit2 } from 'lucide-react';
import Button from './components/Button';

const ShiftScheduleView = ({ entries, employees, t, lang, onAddShift, onDeleteShift, onEditShift, isAdmin }) => {
    // Selected shift for details popup
    const [selectedShift, setSelectedShift] = useState(null);
    // Header controls toggle for mobile
    const [showControls, setShowControls] = useState(false);


    // --- CONSTANTS ---
    const TIME_SLOTS = ['8:00', '8:30', '9:00', '9:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30'];
    const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    // Default fallback colors if no user color set
    const DEFAULT_COLOR = '#00FF9C'; // Accent Mint

    // Week navigation state
    const [currentWeekStart, setCurrentWeekStart] = React.useState(() => {
        const now = new Date();
        const day = now.getDay();
        const diff = now.getDate() - (day === 0 ? 6 : day - 1);
        const monday = new Date(now);
        monday.setDate(diff);
        monday.setHours(0, 0, 0, 0);
        return monday;
    });

    const weekDates = useMemo(() => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(currentWeekStart);
            d.setDate(currentWeekStart.getDate() + i);
            days.push(d);
        }
        return days;
    }, [currentWeekStart]);

    const navigateWeek = (direction) => {
        const newStart = new Date(currentWeekStart);
        newStart.setDate(currentWeekStart.getDate() + (direction * 7));
        setCurrentWeekStart(newStart);
    };

    // Helper to get local YYYY-MM-DD string
    const getLocalDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Filter entries for this week
    const weekEntries = useMemo(() => {
        const startStr = getLocalDateString(weekDates[0]);
        const endStr = getLocalDateString(weekDates[6]);
        return entries.filter(e => e.date >= startStr && e.date <= endStr);
    }, [entries, weekDates]);

    // Helper to calculate duration in hours if missing
    const getDuration = (start, end) => {
        if (!start || !end) return 0;
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        return (eh + em / 60) - (sh + sm / 60);
    };

    // Build DYNAMIC employee assignments per day
    const dayAssignments = useMemo(() => {
        const result = {};
        weekDates.forEach((date, idx) => {
            const dateStr = getLocalDateString(date);
            const dayEntries = weekEntries.filter(e => e.date === dateStr);
            // Unique employees for this day
            const uniqueEmployeeIds = [...new Set(dayEntries.map(e => e.employeeId || e.userId))];

            if (uniqueEmployeeIds.length === 0) {
                result[idx] = [];
            } else {
                result[idx] = uniqueEmployeeIds.map((empId, slotIdx) => ({
                    empId,
                    slotIndex: slotIdx,
                    employee: employees.find(emp => emp.id === empId),
                    entries: dayEntries.filter(e => (e.employeeId || e.userId) === empId)
                }));
            }
        });
        return result;
    }, [weekDates, weekEntries, employees]);

    // Helper to check if a half-hour slot is covered by an entry
    const isSlotCovered = (entry, slotTime) => {
        const [slotH, slotM] = slotTime.split(':').map(Number);
        const slotDecimal = slotH + slotM / 60;

        const [startH, startM] = (entry.startTime || '').split(':').map(Number);
        const [endH, endM] = (entry.endTime || '').split(':').map(Number);

        if (isNaN(startH) || isNaN(endH)) return false;

        const startDecimal = startH + startM / 60;
        const endDecimal = endH + endM / 60;

        return slotDecimal >= startDecimal && slotDecimal < endDecimal;
    };

    // Get week number
    const getWeekNumber = (d) => {
        const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    };

    // Calculate total hours
    const calculateDayTotal = (entriesArr) => {
        return entriesArr.reduce((sum, e) => sum + (Number(e.duration) || getDuration(e.startTime, e.endTime)), 0);
    };

    return (
        <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-gray-200 dark:border-zinc-800 overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900/50">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 uppercase tracking-wide">
                        {t('scheduleTab') || 'SCHICHTPLAN'}
                        <span className="ml-2 text-sm font-normal text-gray-500">
                            WEEK {getWeekNumber(currentWeekStart)}
                        </span>
                    </h2>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowControls(!showControls)}
                        className="md:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-gray-200 focus:outline-none"
                    >
                        {showControls ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </Button>
                </div>

                <div className={`${showControls ? 'flex' : 'hidden'} md:flex flex-col md:flex-row items-center justify-end gap-3 mt-4 md:mt-0 w-full md:w-auto`}>
                    {/* Add Shift Button (Admin only) */}
                    {isAdmin && onAddShift && (
                        <Button
                            variant="primary"
                            onClick={onAddShift}
                            className="w-full md:w-auto flex items-center justify-center gap-2 font-medium text-sm shadow-sm transition-colors whitespace-nowrap"
                        >
                            <Plus size={16} />
                            {t('addShift') || 'Schicht hinzufügen'}
                        </Button>
                    )}
                    {/* Week Navigation */}
                    <div className="w-full md:w-auto flex items-center justify-center bg-white dark:bg-zinc-800 rounded-md shadow-sm border border-gray-200 dark:border-zinc-700">
                        <Button variant="ghost" size="icon" onClick={() => navigateWeek(-1)} className="hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-l flex-1 md:flex-none flex justify-center">
                            <ChevronLeft size={18} />
                        </Button>
                        <div className="px-3 text-sm font-medium text-gray-700 dark:text-slate-300 min-w-[100px] text-center flex items-center justify-center">
                            {weekDates[0].toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} - {weekDates[6].toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => navigateWeek(1)} className="hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-r flex-1 md:flex-none flex justify-center">
                            <ChevronRight size={18} />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Schedule Table */}
            <div className="overflow-x-auto w-full">
                <table className="w-auto border-collapse text-xs table-fixed">
                    <thead>
                        {/* Row 1: Day Names */}
                        <tr className="bg-gray-100 dark:bg-zinc-800">
                            <th className="border border-gray-300 dark:border-zinc-600 p-1 w-16 sticky left-0 bg-gray-100 dark:bg-zinc-800 z-10"></th>
                            {DAYS.map((day, dayIdx) => {
                                const empCount = dayAssignments[dayIdx]?.length || 1;
                                return (
                                    <th key={day} colSpan={empCount} className={`border-b border-gray-300 dark:border-zinc-600 p-2 text-center font-semibold text-gray-700 dark:text-slate-300 border-r border-gray-300 dark:border-zinc-600 ${dayIdx > 0 ? 'border-l-[3px] border-l-gray-400 dark:border-l-gray-500' : 'border-l border-gray-300'} ${dayIdx === 1 ? 'bg-stripe-diagonal dark:bg-zinc-800/80 bg-gray-100' : ''}`}>
                                        <div>{day}</div>
                                        <div className="text-[10px] font-normal text-gray-500 dark:text-slate-400 leading-tight">
                                            {weekDates[dayIdx].toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                        </div>
                                        {dayIdx === 1 && <div className="text-[9px] text-red-500 font-bold uppercase mt-1">Zu</div>}
                                    </th>
                                );
                            })}
                        </tr>

                        {/* Row 2: Vertical Employee Names */}
                        <tr className="bg-gray-5 dark:bg-zinc-900 h-24">
                            <th className="border border-gray-300 dark:border-zinc-600 p-1 sticky left-0 bg-gray-50 dark:bg-zinc-900 z-10"></th>
                            {DAYS.map((day, dayIdx) => {
                                const assignments = dayAssignments[dayIdx] || [];
                                if (assignments.length === 0) {
                                    return (
                                        <th key={`header-empty-${dayIdx}`} className={`w-6 min-w-[24px] border-b border-r border-gray-300 dark:border-zinc-600 p-0 align-bottom ${dayIdx > 0 ? 'border-l-[3px] border-l-gray-400 dark:border-l-gray-500' : 'border-l border-gray-300'} ${dayIdx === 1 ? 'bg-gray-100 dark:bg-zinc-800/80' : ''}`}>
                                            <div className="flex flex-col items-center justify-end h-full pb-1 text-gray-300">-</div>
                                        </th>
                                    );
                                }
                                return assignments.map((assignment, slotIdx) => {
                                    const empName = assignment.employee?.firstName || assignment.employee?.name?.split(' ')[0] || '?';
                                    return (
                                        <th key={`header-${dayIdx}-${slotIdx}`} className={`w-6 min-w-[24px] border-b border-r border-gray-300 dark:border-zinc-600 p-0 align-bottom ${dayIdx > 0 && slotIdx === 0 ? 'border-l-[3px] border-l-gray-400 dark:border-l-gray-500' : 'border-l-0'} ${dayIdx === 1 ? 'bg-gray-100 dark:bg-zinc-800/80' : ''}`} title={assignment.employee?.name || 'Unknown'}>
                                            <div className="flex items-end justify-center h-full w-full pb-1">
                                                <span className="text-[10px] text-gray-600 dark:text-slate-300 font-bold uppercase whitespace-nowrap" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                                                    {empName}
                                                </span>
                                            </div>
                                        </th>
                                    );
                                });
                            })}
                        </tr>



                    </thead>
                    <tbody>
                        {TIME_SLOTS.map((timeSlot, slotIdx) => (
                            <tr key={timeSlot} className="hover:bg-gray-50 dark:hover:bg-zinc-800/50">
                                {/* Time Label */}
                                <td className="border border-gray-300 dark:border-zinc-600 p-1 text-right font-medium text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-zinc-900 whitespace-nowrap sticky left-0 z-10">
                                    {timeSlot}
                                </td>

                                {/* Day Cells */}
                                {DAYS.map((day, dayIdx) => {
                                    const assignments = dayAssignments[dayIdx] || [];
                                    if (assignments.length === 0) {
                                        return (
                                            <td key={`cell-empty-${dayIdx}-${slotIdx}`} className={`p-0 h-4 min-w-[24px] ${dayIdx > 0 ? 'border-l-[3px] border-l-gray-400 dark:border-l-gray-500' : 'border-l border-gray-300'} ${dayIdx === 1 ? 'bg-gray-100 dark:bg-zinc-900/40 pattern-diagonal-lines pattern-gray-200 pattern-bg-white pattern-size-4 pattern-opacity-20' : ''}`}></td>
                                        );
                                    }
                                    return assignments.map((assignment, empIdx) => {
                                        const coveredEntry = assignment.entries.find(entry => isSlotCovered(entry, timeSlot));
                                        const isCovered = !!coveredEntry;
                                        let bgColor = 'transparent';
                                        let cellContent = null;

                                        if (isCovered) {
                                            if (coveredEntry.type === 'SICK') {
                                                bgColor = '#FF7F6C'; // Accent Coral
                                                cellContent = <span className="text-[10px] leading-none text-black animate-pulse">🤒</span>;
                                            }
                                            else if (coveredEntry.type === 'VACATION') {
                                                bgColor = '#eab308'; // Yellow-500
                                                cellContent = <span className="text-[10px] leading-none">🏖️</span>;
                                            }
                                            else if (coveredEntry.type === 'TIME_TRACKING') {
                                                bgColor = '#D8B4FE'; // Accent Lilac
                                                cellContent = <span className="text-[10px] leading-none text-black">⏱️</span>;
                                            }
                                            else {
                                                bgColor = assignment.employee?.color || DEFAULT_COLOR;
                                            }
                                        }

                                        return (
                                            <td
                                                key={`cell-${dayIdx}-${slotIdx}-${empIdx}`}
                                                className={`p-0 h-4 align-middle text-center ${isCovered && isAdmin ? 'cursor-pointer hover:opacity-80' : ''} ${dayIdx > 0 && empIdx === 0 ? 'border-l-[3px] border-l-gray-400 dark:border-l-gray-500' : 'border-l-0'} ${dayIdx === 1 ? 'bg-gray-100 dark:bg-zinc-900/40' : ''}`}
                                                style={{ backgroundColor: bgColor !== 'transparent' ? bgColor : undefined }}
                                                onClick={() => {
                                                    if (isCovered && isAdmin && coveredEntry) {
                                                        setSelectedShift({ entry: coveredEntry, employee: assignment.employee });
                                                    }
                                                }}
                                                title={isCovered ? `${assignment.employee?.firstName || 'Unknown'}: ${coveredEntry.startTime} - ${coveredEntry.endTime}${coveredEntry.type === 'SICK' ? ' (Krank)' : coveredEntry.type === 'VACATION' ? ' (Urlaub)' : coveredEntry.type === 'TIME_TRACKING' ? ' (Zeit erfassen)' : ''}` : ''}
                                            >
                                                {cellContent}
                                            </td>
                                        );
                                    });
                                })}
                            </tr>
                        ))}

                        {/* Footer: Total Hours */}
                        <tr className="bg-gray-100 dark:bg-zinc-800 font-bold">
                            <td className="border border-gray-300 dark:border-zinc-600 p-2 text-right text-gray-700 dark:text-slate-300 whitespace-nowrap sticky left-0 bg-gray-100 dark:bg-zinc-800 z-10">
                                Gesamt
                            </td>
                            {DAYS.map((day, dayIdx) => {
                                const assignments = dayAssignments[dayIdx] || [];
                                if (assignments.length === 0) {
                                    return (
                                        <td key={`total-empty-${dayIdx}`} className={`border-b border-r border-gray-300 dark:border-zinc-600 p-1 text-center text-gray-400 ${dayIdx > 0 ? 'border-l-[3px] border-l-gray-400 dark:border-l-gray-500' : 'border-l border-gray-300'} ${dayIdx === 1 ? 'bg-gray-100 dark:bg-zinc-800' : ''}`}>-</td>
                                    );
                                }
                                return assignments.map((assignment, empIdx) => {
                                    const total = calculateDayTotal(assignment.entries);
                                    return (
                                        <td key={`total-${dayIdx}-${empIdx}`} className={`border-b border-r border-gray-300 dark:border-zinc-600 p-0 text-center text-[10px] text-gray-600 dark:text-slate-400 ${dayIdx > 0 && empIdx === 0 ? 'border-l-[3px] border-l-gray-400 dark:border-l-gray-500' : 'border-l-0'} ${dayIdx === 1 ? 'bg-gray-100 dark:bg-zinc-800' : ''}`}>
                                            {total > 0 ? total.toFixed(1) : ''}
                                        </td>
                                    );
                                });
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Shift Details Popup */}
            {selectedShift && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedShift(null)}>
                    <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Schicht Details</h3>
                            <Button variant="ghost" size="iconSm" onClick={() => setSelectedShift(null)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </Button>
                        </div>

                        <div className="space-y-3 mb-6">
                            <div>
                                <div className="text-xs text-gray-500 uppercase">Mitarbeiter</div>
                                <div className="font-semibold text-gray-800 dark:text-white">{selectedShift.employee?.name || 'Unbekannt'}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 uppercase">Datum</div>
                                <div className="font-semibold text-gray-800 dark:text-white">{selectedShift.entry.date}</div>
                            </div>
                            <div className="flex gap-4">
                                <div>
                                    <div className="text-xs text-gray-500 uppercase">Von</div>
                                    <div className="font-semibold text-gray-800 dark:text-white">{selectedShift.entry.startTime}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 uppercase">Bis</div>
                                    <div className="font-semibold text-gray-800 dark:text-white">{selectedShift.entry.endTime}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 uppercase">Dauer</div>
                                    <div className="font-semibold text-gray-800 dark:text-white">
                                        {(Number(selectedShift.entry.duration) || getDuration(selectedShift.entry.startTime, selectedShift.entry.endTime)).toFixed(1)}h
                                    </div>
                                </div>
                            </div>
                        </div>


                        <div className="flex gap-2">
                            {isAdmin && onEditShift && (
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        onEditShift(selectedShift.entry);
                                        setSelectedShift(null);
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 font-medium"
                                >
                                    <Edit2 size={16} />
                                    Bearbeiten
                                </Button>
                            )}
                            {isAdmin && onDeleteShift && (
                                <Button
                                    variant="destructive"
                                    onClick={() => {
                                        if (window.confirm('Schicht wirklich löschen?')) {
                                            onDeleteShift(selectedShift.entry);
                                            setSelectedShift(null);
                                        }
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 font-medium"
                                >
                                    <Trash2 size={16} />
                                    Löschen
                                </Button>
                            )}
                        </div>
                    </div>
                </div >
            )}

            {/* Legend */}
            <div className="p-4 border-t border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900/50">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Legende</h3>
                <div className="flex flex-wrap gap-3">
                    {employees.map(emp => (
                        <div key={emp.id} className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: emp.color || DEFAULT_COLOR }}></div>
                            <span className="text-xs font-medium text-gray-700 dark:text-slate-300">{emp.firstName || emp.name}</span>
                        </div>
                    ))}
                    <div className="flex items-center gap-2 ml-4 border-l pl-4 dark:border-slate-700">
                        <div className="w-3 h-3 rounded-full bg-accent-coral shadow-sm"></div>
                        <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Krankheit</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-sm"></div>
                        <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Urlaub</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-accent-lilac shadow-sm"></div>
                        <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Zeit erfassen</span>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default ShiftScheduleView;
